import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BarChart3, Search, Users, MapPin, Activity, ClipboardCheck, LineChart, ChevronRight, Building2, FileText } from "lucide-react";
import api, { formatErr } from "../lib/api";
import PerformanceBlock from "../components/perf/PerformanceBlock";
import PlantSLABoard from "../components/perf/PlantSLABoard";
import PerfEditRequestsInbox from "../components/perf/PerfEditRequestsInbox";
import PlantReportView from "./PlantReportView";
import { useAuth } from "../context/AuthContext";

/**
 * Performance — Iter 367 (Phase B) standalone hub page.
 *
 * Lists customers (with search) and lets the user drill into one
 * customer's PerformanceBlock. Deep-link via ?customer=<id>.
 */
export default function Performance() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("action.edit_customers");
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // Iter 400.19 — controls the top-search picker dropdown visibility.
  const [pickerOpen, setPickerOpen] = useState(false);
  // Iter 400.24 — family-tree breadcrumb (Customer → Site chips).
  const [selectedSites, setSelectedSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [params, setParams] = useSearchParams();
  // Iter 400 — top-level tabs: electronic logbook (existing) vs Plant SLA (new).
  // Iter 400.15 — third tab: portal edit-request approvals.
  // Deep-linkable via ?view=sla|edit-requests so a notification / bookmark can land on it.
  const view = params.get("view");
  const currentView =
    view === "sla" ? "sla" :
    view === "edit-requests" ? "edit-requests" :
    view === "analytics" ? "analytics" :
    view === "report" ? "report" :
    "logs";
  const setView = (v) => {
    const next = new URLSearchParams(params);
    if (v === "logs") next.delete("view");
    else next.set("view", v);
    setParams(next);
  };
  const autoReqId = params.get("req") || null;
  // Iter 400.15 — pending edit-request count for the tab badge.
  const [pendingCount, setPendingCount] = useState(0);
  const refreshPendingCount = () => {
    api
      .get("/perf/readings/edit-requests?status=pending")
      .then(({ data }) => setPendingCount(data?.count || 0))
      .catch(() => {});
  };
  useEffect(() => {
    refreshPendingCount();
    // Refresh count whenever the user switches to the approvals tab.
  }, [currentView]);
  const selectedId = params.get("customer") || null;
  const selected = customers.find((c) => c.id === selectedId);

  // Iter 418.ax (Dirk, 2026-02-28) — Track recently-viewed customers
  // in localStorage so the user can jump back with one click instead
  // of searching every time. Most-recent first, cap 8.
  const RECENT_KEY = "perf.recentCustomers";
  const [recentIds, setRecentIds] = useState(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  useEffect(() => {
    if (!selectedId) return;
    setRecentIds((prev) => {
      const next = [selectedId, ...prev.filter((x) => x !== selectedId)].slice(0, 8);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [selectedId]);
  const recentlyViewed = useMemo(
    () => recentIds
      .map((id) => customers.find((c) => c.id === id))
      .filter(Boolean),
    [recentIds, customers],
  );

  // Iter 400.24 — Fetch sites for the current customer to render the
  // Customer → Site(s) family-tree breadcrumb strip below the picker.
  useEffect(() => {
    if (!selectedId) { setSelectedSites([]); return; }
    let alive = true;
    setSitesLoading(true);
    api.get(`/sites?customer_id=${selectedId}`)
      .then(({ data }) => { if (alive) setSelectedSites(Array.isArray(data) ? data : (data?.items || [])); })
      .catch(() => { if (alive) setSelectedSites([]); })
      .finally(() => { if (alive) setSitesLoading(false); });
    return () => { alive = false; };
  }, [selectedId]);
  // Iter 381 — deep link from a "perf_warn / perf_alarm" notification
  // arrives as ?customer=&site=&equipment=&parameter=&date= so the
  // register view can auto-scroll to the exact row that fired.
  const deepLink = useMemo(() => {
    const site = params.get("site");
    const eqid = params.get("equipment");
    const pid = params.get("parameter");
    const d = params.get("date");
    if (!site && !eqid && !pid && !d) return null;
    return { site, equipment: eqid, parameter: pid, date: d };
  }, [params]);

  useEffect(() => {
    api.get("/customers")
      .then(({ data }) => {
        // Iter 418.as (Dirk, 2026-02-28) — Show ALL customers, not just
        // performance-enabled ones. Operators need to search a customer
        // AND toggle Perf ON right from here without hopping to the
        // customer profile. Enabled/disabled state is used to sort +
        // colour the row.
        const list = Array.isArray(data) ? data : (data?.items || []);
        setCustomers(list);
      })
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, []);

  // Iter 418.aj — Sort by `last_perf_reading_at` (most recent captures
  // first) so the picker's default view surfaces the customers the
  // team is actively working on. Falls back to name-sort for
  // customers without any readings yet.
  const sortedByRecent = useMemo(() => {
    return [...customers].sort((a, b) => {
      const ra = a.last_perf_reading_at || "";
      const rb = b.last_perf_reading_at || "";
      if (ra && rb) return rb.localeCompare(ra);
      if (ra) return -1;
      if (rb) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [customers]);

  const filtered = sortedByRecent.filter((c) =>
    !q || (c.name || "").toLowerCase().includes(q.toLowerCase()),
  );
  const recentCustomers = useMemo(
    () => sortedByRecent.filter((c) => c.last_perf_reading_at).slice(0, 5),
    [sortedByRecent],
  );

  return (
    <div className="space-y-4" data-testid="performance-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Water Treatment</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#22d3ee]" /> Performance
          </h1>
          <p className="text-xs text-[#a0aabf] mt-1">
            Electronic log books — track daily readings across sites, equipment and parameters.
          </p>
        </div>
      </div>

      {err && <div className="hv-card border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">{err}</div>}

      {/* Iter 400 — top-level view tabs. Logs = existing per-customer
          electronic logbook. SLA = tenant-wide plant uptime rollup.
          Iter 400.15 — Approvals = pending portal edit-requests inbox. */}
      <div className="flex items-center gap-1 bg-white/[0.02] border border-white/10 rounded-md p-1 w-fit" data-testid="perf-view-tabs">
        <button
          type="button"
          onClick={() => setView("logs")}
          className={`px-3 py-1.5 rounded text-xs font-medium transition inline-flex items-center gap-1.5 ${
            currentView === "logs" ? "bg-[#22d3ee]/20 text-[#22d3ee]" : "text-[#a0aabf] hover:text-white"
          }`}
          data-testid="perf-view-logs"
        >
          <BarChart3 className="w-3.5 h-3.5" /> Logbook
        </button>
        <button
          type="button"
          onClick={() => setView("analytics")}
          className={`px-3 py-1.5 rounded text-xs font-medium transition inline-flex items-center gap-1.5 ${
            currentView === "analytics" ? "bg-[#A78BFA]/20 text-[#A78BFA]" : "text-[#a0aabf] hover:text-white"
          }`}
          data-testid="perf-view-analytics"
        >
          <LineChart className="w-3.5 h-3.5" /> Analytics
        </button>
        <button
          type="button"
          onClick={() => setView("sla")}
          className={`px-3 py-1.5 rounded text-xs font-medium transition inline-flex items-center gap-1.5 ${
            currentView === "sla" ? "bg-[#22d3ee]/20 text-[#22d3ee]" : "text-[#a0aabf] hover:text-white"
          }`}
          data-testid="perf-view-sla"
        >
          <Activity className="w-3.5 h-3.5" /> Plant Uptime
        </button>
        {/* Iter 418.ar (Dirk, 2026-02-28) — Plant Report tab PARKED.
            Feature needs a rebuild before it's useful. Leaving the
            button commented rather than deleting so re-enabling is a
            one-line change once the metric model is redesigned.
        <button ... perf-view-report ...>Plant Report</button>
        */}
        <button
          type="button"
          onClick={() => setView("edit-requests")}
          className={`px-3 py-1.5 rounded text-xs font-medium transition inline-flex items-center gap-1.5 ${
            currentView === "edit-requests" ? "bg-amber-500/20 text-amber-300" : "text-[#a0aabf] hover:text-white"
          }`}
          data-testid="perf-view-edit-requests"
        >
          <ClipboardCheck className="w-3.5 h-3.5" /> Approvals
          {pendingCount > 0 && (
            <span
              className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 rounded-full text-[9px] font-bold px-1 bg-amber-500 text-black"
              data-testid="perf-approvals-badge"
            >
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {currentView === "sla" ? (
        <PlantSLABoard customerId={selectedId || null} />
      ) : currentView === "edit-requests" ? (
        <PerfEditRequestsInbox customerId={selectedId} autoOpenReqId={autoReqId} />
      ) : (
        /* Iter 418.ar — `report` view intentionally disabled; falls
           through to the default logbook view if the URL still has
           ?view=report from a bookmarked link.
           Iter 400.19 — Full-width Logbook view. */
      <div className="space-y-3 min-w-0">
        <div className="hv-card p-3 flex flex-col md:flex-row md:items-center gap-2">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] md:min-w-[110px]">
            Customer
          </div>
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-[#647087]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setPickerOpen(true)}
              onBlur={() => setTimeout(() => setPickerOpen(false), 150)}
              placeholder={selected ? "Change customer…" : "Search customers…"}
              className="hv-input pl-7 w-full text-sm"
              data-testid="perf-page-search"
            />
            {pickerOpen && (
              <div
                className="absolute left-0 right-0 top-full mt-1 z-30 hv-card p-1 max-h-[60vh] overflow-auto shadow-xl"
                data-testid="perf-page-picker"
              >
                {loading && (
                  <div className="text-xs text-[#647087] p-2">Loading customers…</div>
                )}
                {!loading && filtered.length === 0 && (
                  <div className="text-xs text-[#647087] p-2">No matches.</div>
                )}
                {filtered.slice(0, 40).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setParams({ customer: c.id });
                      setQ("");
                      setPickerOpen(false);
                    }}
                    className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 transition ${selectedId === c.id ? "bg-[#22d3ee]/10 text-white" : "text-[#a0aabf] hover:bg-white/[0.03] hover:text-white"}`}
                    data-testid={`perf-page-customer-${c.id}`}
                  >
                    <Users className="w-3.5 h-3.5 shrink-0 opacity-70" />
                    <span className="truncate">{c.name}</span>
                  </button>
                ))}
                {filtered.length > 40 && (
                  <div className="text-[10px] text-[#647087] px-2 py-1">
                    {filtered.length - 40} more · refine your search
                  </div>
                )}
              </div>
            )}
          </div>
          {selected && (
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-display text-sm font-semibold truncate max-w-[220px]" data-testid="perf-page-current-customer">
                {selected.name}
              </span>
              <Link
                to={`/customers/${selected.id}`}
                className="text-[11px] text-[#22d3ee] hover:underline"
                data-testid="perf-page-customer-link"
              >
                Open customer →
              </Link>
            </div>
          )}
        </div>

        {/* Iter 400.24 — Family-tree breadcrumb: Customer → Sites.
            Each site chip scrolls the register into view so the user
            can eyeball which site they're logging against. */}
        {selected && (
          <div
            className="hv-card px-3 py-2 flex flex-wrap items-center gap-1.5 text-[12px]"
            data-testid="perf-page-family-tree"
          >
            <Users className="w-3.5 h-3.5 text-[#22d3ee] shrink-0" />
            <Link
              to={`/customers/${selected.id}`}
              className="font-semibold hover:text-[#22d3ee] transition"
              data-testid="perf-family-customer"
            >
              {selected.name}
            </Link>
            <ChevronRight className="w-3 h-3 text-[#647087] shrink-0" />
            {sitesLoading && (
              <span className="text-[#647087]">loading sites…</span>
            )}
            {!sitesLoading && selectedSites.length === 0 && (
              <span className="text-[#647087] italic">No sites yet</span>
            )}
            {!sitesLoading && selectedSites.map((s, i) => (
              <span key={s.id} className="inline-flex items-center gap-1">
                {i > 0 && <span className="text-[#647087]">·</span>}
                <button
                  type="button"
                  onClick={() => {
                    const el = document.querySelector(`[data-perf-site-anchor="${s.id}"]`);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.03] hover:border-[#22d3ee] hover:text-white transition text-[#a0aabf]"
                  data-testid={`perf-family-site-${s.id}`}
                  title={s.town ? `${s.name} · ${s.town}` : s.name}
                >
                  <Building2 className="w-2.5 h-2.5" />
                  <span className="truncate max-w-[140px]">{s.name}</span>
                </button>
              </span>
            ))}
          </div>
        )}

        {selected ? (
          <PerformanceBlock
            key={`${selected.id}-${currentView}`}
            customerId={selected.id}
            canEdit={canEdit}
            canCapture
            deepLink={deepLink}
            visibleSection={currentView === "analytics" ? "analytics" : "logbook"}
          />
        ) : (
          <div className="space-y-3">
            <div className="hv-card p-6 text-center text-sm text-[#647087] flex flex-col items-center gap-3">
              <MapPin className="w-8 h-8 text-[#22d3ee]/50" />
              Search and pick a customer above to view or capture readings.
            </div>
            {/* Iter 418.ax (Dirk, 2026-02-28) — Recently VIEWED
                customers (localStorage) so operators jump straight
                back into the plant they were just looking at. Sits
                above the auto "Recently active" list which is driven
                by last_perf_reading_at. */}
            {recentlyViewed.length > 0 && (
              <div className="hv-card p-3" data-testid="perf-recently-viewed">
                <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-2 flex items-center gap-1">
                  <Users className="w-3 h-3" /> Recently viewed
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {recentlyViewed.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setParams({ customer: c.id })}
                      className="text-[11px] px-2.5 py-1 rounded border border-white/10 hover:border-emerald-500/40 hover:bg-emerald-500/5 transition flex items-center gap-1"
                      data-testid={`perf-recently-viewed-${c.id}`}
                    >
                      <Users className="w-3 h-3 text-emerald-300/70" />
                      <span className="truncate max-w-[220px]">{c.name}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setRecentIds([]);
                      try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ }
                    }}
                    className="text-[10px] px-2 py-1 rounded text-[#647087] hover:text-rose-300 transition"
                    data-testid="perf-recently-viewed-clear"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
            {recentCustomers.length > 0 && (
              <div className="hv-card p-3" data-testid="perf-recent-customers">
                <div className="text-[10px] uppercase tracking-wider text-[#22d3ee] font-bold mb-2 flex items-center gap-1">
                  <Activity className="w-3 h-3" /> Recently active plants
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {recentCustomers.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setParams({ customer: c.id })}
                      className="text-left border border-white/5 rounded-md p-2.5 hover:border-[#22d3ee]/40 hover:bg-white/[0.02] transition"
                      data-testid={`perf-recent-${c.id}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <Users className="w-3 h-3 text-[#22d3ee]/70" />
                        <span className="text-sm font-medium truncate flex-1">{c.name}</span>
                      </div>
                      <div className="text-[10px] text-[#647087] mt-0.5">
                        Last reading {new Date(c.last_perf_reading_at).toLocaleDateString()}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!loading && customers.length === 0 && (
              <div className="hv-card p-4 text-sm text-amber-200/90 border-amber-500/30 bg-amber-500/5">
                No customers loaded. Refresh to try again.
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
